/*
 * IMPORTS
 */
import React from 'react' // Npm: React.js library.
import PropTypes from 'prop-types' // Npm: React.js library.
import _ from 'underscore' // Npm: Utility module.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { HiPlay, HiStop } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useBreakpointValue,
  useStyleConfig
} from '@chakra-ui/react' // Npm: A simple, modular and accessible component library for React.js.
import './index.css'


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'


/*
 * GRAPHS
 */
import VendorAccountConnectedQuery from './__query__/index.vendorAccount.connected.query'
import SmppActiveInActiveMutation from './__mutation__/index.smpp.activeInActive.mutation'


/*
 * OBJECTS
 */
function Index({ title, isConnect }) {
  // Const assignment.
  const _successFlags = ['READ_SUCCESSFUL']

  // Hook assignment.
  const [playIsLoading, setPlayIsLoading] = React.useState([])
  const [isCacheClearLoading, setIsCacheClearLoading] = React.useState([])
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  const _isFilterApplied = filterModel?.items?.some(
    (i) => i.value != void 0 && i.value !== "",
  );
  const _appliedFilter = _isFilterApplied ? filterModel : void 0;

  const _queryVariables = React.useMemo(
    () => ({
      isActive: Boolean(isConnect),
      take: paginationModel.pageSize,
      skip: paginationModel.page * paginationModel.pageSize,
      filter: _appliedFilter,
    }),
    [_appliedFilter, isConnect, paginationModel.page, paginationModel.pageSize],
  );
  const [MutationSmppActiveInActive] = useMutation(SmppActiveInActiveMutation)
  const _QueryVendorAccountConnected = useQuery(VendorAccountConnectedQuery, {
    variables: _queryVariables,
    fetchPolicy: Object.React.App.fetchPolicy,
    pollInterval: Object.React.App.pollInterval,
  });

  const _styles = useStyleConfig('Card')
  const _isCurrentViewMobile = useBreakpointValue({
    'base': 'false',
    'md': false,
    'lg': false,
    'xl': false,
    'sm': true,
    'xs': true
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _rowCountRef = React.useRef(0);

  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );

  // Data assignment.
  if (!_QueryVendorAccountConnected.loading && 0 < _QueryVendorAccountConnected.data?.VendorAccountConnected?.length) _isFirstLoadCompleted.current = true
  if (0 === _QueryVendorAccountConnected?.data?.VendorAccountConnected?.length || (0 < _QueryVendorAccountConnected?.data?.VendorAccountConnected?.length && !_.every(_.pluck(_QueryVendorAccountConnected?.data?.VendorAccountConnected, 'status'), j => _successFlags.includes(j)))) _isFirstLoadCompleted.current = false

  // No separate functions - inline logic like original

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 0.5,
        minWidth: 50,
        cellClassName: "cell-font",
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "Smpp.ip",
        headerName: "IP | Action",
        align: "center",
        headerAlign: "center",
        flex: 1.2,
        minWidth: 160,
        sortable: true,
        filterable: true,
        cellClassName: "cell-font",
        renderCell: ({ row }) => (
          <Flex
            direction="column"
            justify="center"
            align="center"
            gap="2px"
            width="100%"
            height="100%"
          >
            <Text
              color="rgba(43, 54, 116, 1)"
              fontWeight="700"
              fontSize="13px"
              lineHeight="16px"
              textAlign="center"
              width="100%"
            >
              {row['Smpp.ip']}
            </Text>
            <Flex justify="center" align="center" width="100%">
              <Button
                my="0px"
                height="10px"
                minW="18px"
                _hover={{ bg: "none" }}
                _active={{ bg: "none" }}
                bg="none"
                onClick={async () => {
                  // If smpp is active then deactivate it.
                  setPlayIsLoading((i) => [...i, row.smppId]);

                  // If given smpp is active then deactivate it else activate it.
                  if (row.isActive) {
                    // Mutation to deactivate the smpp.
                    const MutationSmppActiveInActiveResponse =
                      await MutationSmppActiveInActive({
                        variables: { smppId: row.smppId, isActive: false },
                      });

                    // Show toast.
                    toast(
                      MutationSmppActiveInActiveResponse?.data
                        ?.SmppActiveInActive?.message ??
                        "Successfully destroyed the smpp.",
                    );

                    // Remove the smpp id from the loading array.
                    return setPlayIsLoading((i) =>
                      i.filter((j) => j !== row.smppId),
                    );
                  }

                  // Activate the smpp.
                  const MutationSmppActiveInActiveResponse =
                    await MutationSmppActiveInActive({
                      variables: { smppId: row.smppId, isActive: true },
                    });

                  // If smpp is inactive then activate it.
                  toast(
                    MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive
                      ?.message ?? "Successfully booted the smpp.",
                  );

                  // Remove the smpp id from the loading array.
                  return setPlayIsLoading((i) =>
                    i.filter((j) => j !== row.smppId),
                  );
                }}
                px="0"
                py="0"
                color="green.400"
              >
                {playIsLoading.includes(row.smppId) ? (
                  <Spinner size="sm" />
                ) : row.isActive ? (
                  <HiStop size={17} />
                ) : (
                  <HiPlay size={17} />
                )}
              </Button>
              <Button
                my="0px"
                height="10px"
                minW="auto"
                pl="10px"
                borderLeft="2px solid var(--chakra-colors-gray-300)"
                borderRadius={0}
                _hover={{ bg: "none" }}
                _active={{ bg: "none" }}
                bg="none"
                onClick={async () => {
                  // If smpp is active then deactivate it.
                  setIsCacheClearLoading((i) => [...i, row.smppId]);

                  // Activate the smpp.
                  const MutationSmppActiveInActiveResponse =
                    await MutationSmppActiveInActive({
                      variables: {
                        smppId: row.smppId,
                        isActive: false,
                        clearSmppBuild: true,
                      },
                    });

                  // If smpp is inactive then activate it.
                  toast(
                    MutationSmppActiveInActiveResponse?.data?.SmppActiveInActive
                      ?.message ?? "Successfully cleared the smpp cache.",
                  );

                  // Remove the smpp id from the loading array.
                  return setIsCacheClearLoading((i) =>
                    i.filter((j) => j !== row.smppId),
                  );
                }}
                px="0"
                py="0"
                color="tomato"
              >
                {isCacheClearLoading.includes(row.smppId) ? (
                  <Text fontSize={12} fontWeight={700}>
                    PLEASE WAIT
                  </Text>
                ) : (
                  <Text fontSize={12} fontWeight={700}>
                    CLEAN CACHE
                  </Text>
                )}
              </Button>
            </Flex>
          </Flex>
        ),
      },
      {
        field: "displayName",
        headerName: "Account",
        align: "center",
        headerAlign: "center",
        flex: 1.5,
        minWidth: 150,
        filterable: true,
        sortable: false,
        cellClassName: "cell-font",
      },
      {
        field: "status",
        headerName: "Status",
        align: "center",
        headerAlign: "center",
        flex: 0.8,
        minWidth: 90,
        disableColumnMenu: true,
        renderCell: () => (
          <Button
            width="80.65px"
            height="22px"
            color={isConnect ? "rgb(32, 32, 32)" : "rgba(217, 11, 11, 1)"}
            fontSize="13px"
            background={
              isConnect ? "rgba(46, 204, 113, 0.3)" : "rgba(255, 242, 242, 1)"
            }
          >
            {isConnect ? ".Online" : ".Offline"}
          </Button>
        ),
      },
      {
        field: "mode",
        headerName: "Mode",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        flex: 0.7,
        minWidth: 70,
        cellClassName: "cell-font",
      },
    ],
    [MutationSmppActiveInActive, playIsLoading, isCacheClearLoading, isConnect],
  );

  // Define rows
  const rows = React.useMemo(() => {
    return _.compact(_QueryVendorAccountConnected?.data?.VendorAccountConnected?.map((item, index) => {
      if (_.isEmpty(item?.Smpp?.id) || item.status !== 'READ_SUCCESSFUL') return null

      return {
        id:
          item?.Smpp?.id ??
          `vendor-account-connected-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
        sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
        smppId: item?.Smpp?.id,
        'Smpp.ip': item?.Smpp?.ip,
        displayName: item?.displayName,
        isActive: item?.Smpp?.isActive,
        mode: item?.Smpp?.mode,
        status: item?.status,
      };
    }))
  }, [_QueryVendorAccountConnected?.data?.VendorAccountConnected, paginationModel.page, paginationModel.pageSize])

  const _QueryVendorAccountConnectedRowCount = _.first(
    _QueryVendorAccountConnected?.data?.VendorAccountConnected,
  )?._totalCount;
  const rowCount = React.useMemo(() => {
    if (
      undefined !== _QueryVendorAccountConnectedRowCount &&
      null !== _QueryVendorAccountConnectedRowCount
    )
      _rowCountRef.current = _QueryVendorAccountConnectedRowCount;

    return _rowCountRef.current;
  }, [_QueryVendorAccountConnectedRowCount]);
  const _HandleFilterModelChange = React.useCallback(
    (__filterModel) => {
      setPaginationModel((current) => ({ ...current, page: 0 }));
      _setFilterDebounced(__filterModel);
    },
    [_setFilterDebounced],
  );

  // Return component.
  return (
    <Box
      __css={_styles}
      justifyContent="center"
      align="center"
      display="flex"
      direction="column"
      height="100%"
      w="100%"
      p={_isCurrentViewMobile ? "12px" : "22px"}
      gap={_isCurrentViewMobile ? "12px" : "22px"}
      fontSize={["clamp(12px, 1.5vw, 15px)"]}
      boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.17)"
    >
      <Flex justify="space-between" alignItems="center" width="100%">
        <Text
          color="rgba(43,54,116,1)"
          fontWeight="700"
          fontSize={{ base: "sm", md: "md" }}
          textAlign="left"
        >
          {title ?? "NOT CONNECTED VENDOR ACCOUNTS"}
        </Text>
      </Flex>
      <Box width="100%" height="450px" overflowX="auto" overflowY="hidden">
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={45}
          rowCount={rowCount}
          paginationMode="server"
          filterMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          filterModel={filterModel}
          onFilterModelChange={_HandleFilterModelChange}
          pageSizeOptions={[10, 50, 100]}
          sx={{
            "& .MuiDataGrid-root": {
              width: "100%",
              minWidth: "fit-content",
            },
            "& .MuiDataGrid-cell": {
              display: "flex !important",
            },
          }}
          slots={{
            loadingOverlay: () => (
              <TableSpinner
                isLoading={true}
                isEmpty={false}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noResultsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
            noRowsOverlay: () => (
              <TableSpinner
                isLoading={false}
                isEmpty={true}
                chopHeightFromHundredPercentage={
                  _tableHeaderHeightRef?.current?.clientHeight
                }
              />
            ),
          }}
          disableRowSelectionOnClick
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(newModel) =>
            setColumnVisibilityModel(newModel)
          }
          loading={_QueryVendorAccountConnected?.loading}
        />
      </Box>
    </Box>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isConnect': PropTypes.bool,
  'title': PropTypes.string
}


/*
 * EXPORTS
 */
export default Index
